import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/common";
import {
  Wand2, Rocket, Upload, ArrowRight, ArrowLeft, Plus, Trash2, CheckCircle2,
  Building2, Boxes, ShieldQuestion, Users, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { fileToDataUrl, applyBrandColors } from "@/lib/theme";
import MicrosoftIntegration from "@/components/MicrosoftIntegration";

const MODULE_INFO = [
  { k: "schema", icon: "🗓️", label: "Schemaläggning", desc: "Planera & publicera scheman, skiftmönster och bemanningsbehov." },
  { k: "rotation", icon: "🔁", label: "Arbetsrotation", desc: "Varningar för belastning och rättvis rotation mellan stationer." },
  { k: "internrekrytering", icon: "💼", label: "Internrekrytering", desc: "Publicera interna jobb och hantera ansökningar." },
  { k: "enkater", icon: "📋", label: "Enkäter", desc: "Pulsmätningar och medarbetarenkäter." },
  { k: "flode", icon: "📣", label: "Flöde & utskick", desc: "Internt nyhetsflöde och skiftutskick – er kommunikationskanal." },
  { k: "utbildningar", icon: "🎓", label: "Utbildningar", desc: "E-learning & fysiska pass kopplade till kompetensmatrisen." },
  { k: "arbetsmiljo", icon: "❤️", label: "Arbetsmiljö", desc: "Frånvarolarm, trösklar och åtgärdslogg." },
  { k: "uppdrag", icon: "✨", label: "Uppdrag & innehåll", desc: "Beställ innehåll av er kreatör och mät engagemang." },
  { k: "bibliotek", icon: "📚", label: "Policys & rutiner", desc: "Samlad, lätthittad plats för policys och rutiner." },
  { k: "itsupport", icon: "🛡️", label: "IT & Admin-kanal", desc: "Säker kanal där medarbetare kan flagga osäkra mejl och få hjälp." },
  { k: "placement", icon: "🤝", label: "Kund & placering", desc: "Kundföretag, konsultplaceringar och fakturerbar tid (bemanning)." },
];
const ALL_KEYS = MODULE_INFO.map((m) => m.k);
const INDUSTRY_CONFIG = {
  "Lager & logistik": { model: "skift", modules: ["schema", "rotation", "flode", "arbetsmiljo", "bibliotek", "itsupport"] },
  "Tillverkning/produktion": { model: "skift", modules: ["schema", "rotation", "enkater", "flode", "arbetsmiljo", "bibliotek", "itsupport"] },
  "Transport & logistik": { model: "skift", modules: ["schema", "rotation", "flode", "arbetsmiljo", "bibliotek", "itsupport"] },
  "Bygg": { model: "skift", modules: ["schema", "internrekrytering", "flode", "arbetsmiljo", "bibliotek", "itsupport"] },
  "Handel/detaljhandel": { model: "skift", modules: ["schema", "internrekrytering", "enkater", "flode", "utbildningar", "bibliotek", "itsupport"] },
  "Restaurang & hotell": { model: "skift", modules: ["schema", "enkater", "flode", "arbetsmiljo", "utbildningar", "bibliotek", "itsupport"] },
  "Vård & omsorg": { model: "skift", modules: ["schema", "enkater", "arbetsmiljo", "utbildningar", "bibliotek", "itsupport"] },
  "Konsultbolag": { model: "tjanst", modules: ["schema", "internrekrytering", "enkater", "flode", "uppdrag", "utbildningar", "bibliotek", "itsupport"] },
  "Bemanningsbolag": { model: "bemanning", modules: ["schema", "internrekrytering", "enkater", "flode", "uppdrag", "placement", "bibliotek", "itsupport"] },
  "Egenföretagare / solo-konsult": { model: "bemanning", modules: ["placement", "uppdrag", "utbildningar", "flode", "bibliotek", "enkater", "itsupport"] },
  "IT/Tech": { model: "tjanst", modules: ["internrekrytering", "enkater", "flode", "uppdrag", "utbildningar", "bibliotek", "itsupport"] },
  "Utbildning": { model: "tjanst", modules: ["schema", "enkater", "flode", "utbildningar", "bibliotek", "itsupport"] },
  "Tjänst/kontor": { model: "tjanst", modules: ["enkater", "flode", "uppdrag", "utbildningar", "bibliotek", "itsupport"] },
  "Annan verksamhet": { model: "skift", modules: ALL_KEYS },
};
const INDUSTRIES = Object.keys(INDUSTRY_CONFIG);
const SIZES = ["1–20", "21–50", "51–150", "151–500", "500+"];
const SHIFTS = ["Dagtid", "2-skift (dag/kväll)", "3-skift (dygnet runt)", "Kontinuerlig drift", "Blandat"];
const INVITE_ROLES = [
  { v: "hr_admin", l: "HR & Arbetsmiljö" }, { v: "chef", l: "Chef" },
  { v: "teamledare", l: "Teamledare" }, { v: "creator", l: "Kreatör (innehåll)" },
];

function NameList({ items, setItems, placeholder, testid }) {
  const set = (i, v) => setItems(items.map((x, idx) => (idx === i ? v : x)));
  const add = () => setItems([...items, ""]);
  const rm = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : [""]);
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input value={v} onChange={(e) => set(i, e.target.value)} placeholder={placeholder} data-testid={`${testid}-${i}`} />
          <Button type="button" variant="ghost" size="icon" onClick={() => rm(i)} data-testid={`${testid}-rm-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testid}-add`}><Plus className="mr-1 h-4 w-4" />Lägg till</Button>
    </div>
  );
}

function CreateCompany({ onCreated }) {
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [logo, setLogo] = useState("");
  const [colors, setColors] = useState({ color_primary: "#0F2942", color_accent: "#2563EB" });
  const onLogo = async (e) => { const f = e.target.files?.[0]; if (!f) return; try { setLogo(await fileToDataUrl(f)); } catch (err) { toast.error(err.message); } };
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/company", { name, org_number: org, logo_data: logo || null, ...colors });
      applyBrandColors(colors.color_primary, colors.color_accent);
      await onCreated();
      toast.success("Organisationen skapad – nu skräddarsyr vi den");
    } catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="mx-auto max-w-lg">
      <PageHeader title="Välkommen! Låt oss skapa er organisation" icon={Rocket}
        subtitle="Först några grunduppgifter – för att få till rätt färger hjälper det med er företagslogga." />
      <Card className="p-6">
        <form onSubmit={submit} className="space-y-4" data-testid="create-company-form">
          <div><Label htmlFor="cn">Företagsnamn</Label><Input id="cn" value={name} onChange={(e) => setName(e.target.value)} required data-testid="company-name-input" /></div>
          <div><Label htmlFor="on">Organisationsnummer (valfritt)</Label><Input id="on" value={org} onChange={(e) => setOrg(e.target.value)} data-testid="org-number-input" /></div>
          <div>
            <Label>Logotyp (valfritt)</Label>
            <div className="mt-1 flex items-center gap-3">
              <div className="grid h-14 w-14 place-items-center overflow-hidden rounded-xl border bg-secondary">
                {logo ? <img src={logo} alt="logo" className="h-full w-full object-contain" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
              </div>
              <label className="cursor-pointer rounded-md border px-3 py-2 text-sm hover:bg-secondary">Välj bild <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="onboarding-logo" /></label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Primärfärg</Label><input type="color" value={colors.color_primary} onChange={(e) => setColors((c) => ({ ...c, color_primary: e.target.value }))} className="mt-1 h-10 w-full rounded border" data-testid="onboarding-primary" /></div>
            <div><Label>Accentfärg</Label><input type="color" value={colors.color_accent} onChange={(e) => setColors((c) => ({ ...c, color_accent: e.target.value }))} className="mt-1 h-10 w-full rounded border" data-testid="onboarding-accent" /></div>
          </div>
          <Button type="submit" className="w-full" data-testid="create-company-btn">Fortsätt <ArrowRight className="ml-1 h-4 w-4" /></Button>
        </form>
      </Card>
    </div>
  );
}

const STEPS = ["Varumärke", "Verksamhet", "Moduler", "Kommunikation & IT", "Roller", "Klart"];

export default function Skraddare() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const hasCompany = !!user?.company_id;
  const { data: state, reload: reloadState } = useApi(hasCompany ? "/setup/state" : null, [hasCompany]);
  const { data: pricing } = useApi(hasCompany ? "/content/config/pricing" : null, [hasCompany]);

  const [step, setStep] = useState(0);
  const [logo, setLogo] = useState("");
  const [colors, setColors] = useState({ color_primary: user?.color_primary || "#0F2942", color_accent: user?.color_accent || "#2563EB" });
  const [profile, setProfile] = useState({ industry: "", size: "", shift_model: "", notes: "" });
  const [facilities, setFacilities] = useState([""]);
  const [departments, setDepartments] = useState([""]);
  const [teams, setTeams] = useState([""]);
  const [modules, setModules] = useState(ALL_KEYS);
  const [itChannel, setItChannel] = useState({ enabled: true, contact_name: "", contact_email: "" });
  const [invites, setInvites] = useState([{ email: "", name: "", role: "hr_admin" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state) return;
    if (state.setup_profile && Object.keys(state.setup_profile).length) setProfile((p) => ({ ...p, ...state.setup_profile }));
    if (Array.isArray(state.enabled_modules)) setModules(state.enabled_modules);
    if (state.it_channel) setItChannel((c) => ({ ...c, ...state.it_channel }));
  }, [state]);

  if (!hasCompany) return <CreateCompany onCreated={refreshUser} />;

  const toggleModule = (k) => setModules((m) => (m.includes(k) ? m.filter((x) => x !== k) : [...m, k]));
  const onLogo = async (e) => { const f = e.target.files?.[0]; if (!f) return; try { setLogo(await fileToDataUrl(f)); } catch (err) { toast.error(err.message); } };

  const saveBrand = async () => {
    try {
      const payload = { ...colors };
      if (logo) payload.logo_data = logo;
      await api.patch("/company", payload);
      applyBrandColors(colors.color_primary, colors.color_accent);
      await refreshUser();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const next = async () => {
    if (step === 0) await saveBrand();
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/setup/apply", {
        profile,
        facilities: facilities.filter((x) => x.trim()),
        departments: departments.filter((x) => x.trim()),
        teams: teams.filter((x) => x.trim()),
        enabled_modules: modules,
        it_channel: itChannel,
        org_model: INDUSTRY_CONFIG[profile.industry]?.model || "skift",
      });
      let invited = 0;
      for (const inv of invites) {
        if (!inv.email.trim()) continue;
        try {
          await api.post("/auth/users", { name: inv.name || inv.email, email: inv.email.trim(), role: inv.role, send_invite: true });
          invited++;
        } catch (e) { toast.error(`${inv.email}: ${apiErr(e)}`); }
      }
      await refreshUser();
      toast.success(`Klart! Organisationen är konfigurerad${invited ? ` · ${invited} inbjudan skickad(e)` : ""}`);
      nav("/dashboard");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const setInv = (i, key, v) => setInvites(invites.map((x, idx) => (idx === i ? { ...x, [key]: v } : x)));

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Organisationsskräddare" icon={Wand2}
        subtitle="Svara på några frågor så konfigurerar vi verktyget för just er verksamhet.">
        {state?.wizard_done && <span className="text-xs text-muted-foreground">Du kan justera detta när som helst</span>}
      </PageHeader>

      <Card className="mb-5 p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">Steg {step + 1} av {STEPS.length} · {STEPS[step]}</span>
          <span className="font-head font-bold" data-testid="wizard-step">{Math.round(((step + 1) / STEPS.length) * 100)}%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
          <div className="h-full bg-accent transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
      </Card>

      <Card className="space-y-4 p-6" data-testid={`wizard-panel-${step}`}>
        {step === 0 && (
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Sparkles className="h-4 w-4 text-accent" />För att få till rätt känsla laddar vi upp er logga och färger.</div>
            <div>
              <Label>Logotyp</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="grid h-16 w-16 place-items-center overflow-hidden rounded-xl border bg-secondary">
                  {(logo || user?.company_logo) ? <img src={logo || user.company_logo} alt="logo" className="h-full w-full object-contain" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
                </div>
                <label className="cursor-pointer rounded-md border px-3 py-2 text-sm hover:bg-secondary">Välj bild <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="wizard-logo" /></label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Primärfärg</Label><input type="color" value={colors.color_primary} onChange={(e) => setColors((c) => ({ ...c, color_primary: e.target.value }))} className="mt-1 h-10 w-full rounded border" data-testid="wizard-primary" /></div>
              <div><Label>Accentfärg</Label><input type="color" value={colors.color_accent} onChange={(e) => setColors((c) => ({ ...c, color_accent: e.target.value }))} className="mt-1 h-10 w-full rounded border" data-testid="wizard-accent" /></div>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Bransch</Label>
                <Select value={profile.industry} onValueChange={(v) => setProfile((p) => ({ ...p, industry: v }))}>
                  <SelectTrigger data-testid="wizard-industry"><SelectValue placeholder="Välj…" /></SelectTrigger>
                  <SelectContent>{INDUSTRIES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Antal medarbetare</Label>
                <Select value={profile.size} onValueChange={(v) => setProfile((p) => ({ ...p, size: v }))}>
                  <SelectTrigger data-testid="wizard-size"><SelectValue placeholder="Välj…" /></SelectTrigger>
                  <SelectContent>{SIZES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="sm:col-span-2"><Label>Skiftmodell</Label>
                <Select value={profile.shift_model} onValueChange={(v) => setProfile((p) => ({ ...p, shift_model: v }))}>
                  <SelectTrigger data-testid="wizard-shift"><SelectValue placeholder="Välj…" /></SelectTrigger>
                  <SelectContent>{SHIFTS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div><Label className="flex items-center gap-1"><Building2 className="h-4 w-4" />Anläggningar/lager</Label><div className="mt-1"><NameList items={facilities} setItems={setFacilities} placeholder="t.ex. Lager Nord" testid="wizard-fac" /></div></div>
              <div><Label className="flex items-center gap-1"><Boxes className="h-4 w-4" />Avdelningar</Label><div className="mt-1"><NameList items={departments} setItems={setDepartments} placeholder="t.ex. Plock" testid="wizard-dep" /></div></div>
              <div><Label className="flex items-center gap-1"><Users className="h-4 w-4" />Team</Label><div className="mt-1"><NameList items={teams} setItems={setTeams} placeholder="t.ex. Team A" testid="wizard-team" /></div></div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="text-sm text-muted-foreground">Slå på de delar ni behöver. Ni kan ändra detta senare.</div>
            <div className="flex flex-wrap gap-2" data-testid="wizard-templates">
              <span className="self-center text-xs text-muted-foreground">Snabbval:</span>
              {Object.keys(INDUSTRY_CONFIG).map((ind) => (
                <Button key={ind} type="button" variant={profile.industry === ind ? "default" : "outline"} size="sm"
                  onClick={() => setModules(INDUSTRY_CONFIG[ind].modules)} data-testid={`wizard-template-${ind}`}>{ind}</Button>
              ))}
              {(state?.saved_templates || []).map((tpl) => (
                <Button key={tpl.name} type="button" variant="secondary" size="sm"
                  onClick={() => setModules(tpl.modules)} data-testid={`wizard-saved-${tpl.name}`}>★ {tpl.name}</Button>
              ))}
              <Button type="button" variant="ghost" size="sm" data-testid="wizard-save-template"
                onClick={async () => {
                  const name = window.prompt("Namn på mallen:");
                  if (!name) return;
                  try { await api.post("/setup/templates", { name, modules }); reloadState(); } catch (e) { toast.error(apiErr(e)); }
                }}>+ Spara som mall</Button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {MODULE_INFO.map((m) => {
                const on = modules.includes(m.k);
                return (
                  <div role="button" tabIndex={0} key={m.k} onClick={() => toggleModule(m.k)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleModule(m.k); } }}
                    data-testid={`wizard-module-${m.k}`}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-left transition-colors ${on ? "border-accent bg-accent/5" : "hover:bg-secondary"}`}>
                    <Checkbox checked={on} className="mt-0.5 pointer-events-none" />
                    <div>
                      <div className="text-sm font-medium">{m.icon} {m.label}</div>
                      <div className="text-xs text-muted-foreground">{m.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="text-sm text-muted-foreground">Kommunikations- och informationskanaler (Flöde, Utskick, Enkäter) styrs av modulerna i föregående steg.</div>
            <label className="flex items-start gap-3 rounded-xl border p-3" data-testid="wizard-itchannel-toggle">
              <Checkbox checked={itChannel.enabled} onCheckedChange={(v) => setItChannel((c) => ({ ...c, enabled: !!v }))} className="mt-0.5" />
              <div>
                <div className="flex items-center gap-1 text-sm font-medium"><ShieldQuestion className="h-4 w-4 text-accent" />Säker IT & Admin-kanal</div>
                <div className="text-xs text-muted-foreground">Medarbetare kan flagga osäkra mejl och få hjälp av IT/Admin. Osäkra ärenden hamnar i egen kö och flaggas.</div>
              </div>
            </label>
            {itChannel.enabled && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label>Kontaktnamn IT/Admin (valfritt)</Label><Input value={itChannel.contact_name} onChange={(e) => setItChannel((c) => ({ ...c, contact_name: e.target.value }))} data-testid="wizard-it-name" /></div>
                <div><Label>Kontakt e-post (valfritt)</Label><Input value={itChannel.contact_email} onChange={(e) => setItChannel((c) => ({ ...c, contact_email: e.target.value }))} data-testid="wizard-it-email" /></div>
              </div>
            )}
            <div className="pt-2">
              <div className="mb-2 text-sm font-medium">Microsoft 365 (valfritt)</div>
              <MicrosoftIntegration />
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div className="text-sm text-muted-foreground">Bjud in HR och chefer med e-post (valfritt). De får en länk för att skapa sitt lösenord.</div>
            {invites.map((inv, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_180px_auto]">
                <Input placeholder="Namn" value={inv.name} onChange={(e) => setInv(i, "name", e.target.value)} data-testid={`wizard-invite-name-${i}`} />
                <Input placeholder="E-post" value={inv.email} onChange={(e) => setInv(i, "email", e.target.value)} data-testid={`wizard-invite-email-${i}`} />
                <Select value={inv.role} onValueChange={(v) => setInv(i, "role", v)}>
                  <SelectTrigger data-testid={`wizard-invite-role-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{INVITE_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="ghost" size="icon" onClick={() => setInvites(invites.length > 1 ? invites.filter((_, idx) => idx !== i) : [{ email: "", name: "", role: "hr_admin" }])} data-testid={`wizard-invite-rm-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setInvites([...invites, { email: "", name: "", role: "hr_admin" }])} data-testid="wizard-invite-add"><Plus className="mr-1 h-4 w-4" />Lägg till person</Button>
          </>
        )}

        {step === 5 && (
          <div className="space-y-3" data-testid="wizard-summary">
            <div className="flex items-center gap-2 text-accent"><CheckCircle2 className="h-5 w-5" /><span className="font-medium">Redo att aktivera</span></div>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>Bransch: <b>{profile.industry || "—"}</b> · Storlek: <b>{profile.size || "—"}</b> · Skift: <b>{profile.shift_model || "—"}</b></li>
              <li>Struktur: <b>{facilities.filter((x) => x.trim()).length}</b> anläggningar · <b>{departments.filter((x) => x.trim()).length}</b> avdelningar · <b>{teams.filter((x) => x.trim()).length}</b> team</li>
              <li>Aktiva moduler: <b>{modules.length}</b> st ({modules.map((k) => MODULE_INFO.find((m) => m.k === k)?.label).filter(Boolean).join(", ")})</li>
              <li>IT/Admin-kanal: <b>{itChannel.enabled ? "på" : "av"}</b></li>
              <li>Inbjudningar: <b>{invites.filter((i) => i.email.trim()).length}</b> st</li>
            </ul>
            {(() => {
              const addon = modules.reduce((s, k) => s + (Number(pricing?.module_prices?.[k]) || 0), 0);
              const extras = (pricing?.extras || []).reduce((s, e) => s + (Number(e.price) || 0), 0);
              const total = (Number(pricing?.base_price) || 0) + addon + extras;
              const money = (n) => `${Number(n || 0).toLocaleString("sv-SE")} ${pricing?.currency || "kr"}${pricing?.period || ""}`;
              return (
                <div className="rounded-xl border bg-secondary/40 p-4" data-testid="wizard-cost">
                  <div className="mb-1 text-sm font-semibold">Kostnadsförslag</div>
                  {total > 0 ? (
                    <>
                      <div className="text-sm text-muted-foreground">{pricing?.base_label || "Grundpaket"}: <b>{money(pricing?.base_price)}</b></div>
                      <div className="text-sm text-muted-foreground">Tillval ({modules.length} moduler): <b>{money(addon)}</b></div>
                      <div className="mt-1 text-lg font-head font-bold">Totalt: {money(total)}</div>
                    </>
                  ) : <div className="text-sm text-muted-foreground">{pricing?.note || "Kontakta oss för en offert."}</div>}
                </div>
              );
            })()}
          </div>
        )}

        <div className="flex items-center justify-between border-t pt-4">
          <Button type="button" variant="ghost" onClick={back} disabled={step === 0} data-testid="wizard-back"><ArrowLeft className="mr-1 h-4 w-4" />Tillbaka</Button>
          {step < STEPS.length - 1
            ? <Button type="button" onClick={next} data-testid="wizard-next">Nästa <ArrowRight className="ml-1 h-4 w-4" /></Button>
            : <Button type="button" onClick={finish} disabled={saving} data-testid="wizard-finish"><Rocket className="mr-1 h-4 w-4" />{saving ? "Aktiverar…" : "Slutför & aktivera"}</Button>}
        </div>
      </Card>
    </div>
  );
}
